// Exterior da cidade: árvores, arbustos, cerca-viva, canteiros, postes, fonte, bancos, lixeira,
// hidrante, placa, bicicletário, carros, moto e caixa de correio. GABARITO da Onda 0: 'arvore';
// o resto do lote é a tarefa H7. Pisos de fora (grama, calçada, asfalto…) ficam em tiles.ts.
//
// Vista 3/4 de cima, luz do alto à esquerda: tom 3 no topo e à esquerda, tom 1 embaixo e à
// direita, tom 0 no contorno (o `outline` usa o tom 0 do material).

import type { RampName } from './palette.js';
import { Pix } from './pix.js';
import type { FurnitureDef } from './types.js';

/** Árvore de copa redonda (2×2 tiles de pegada, sobe 3 tiles). */
function tree(): Pix {
  const pix = new Pix(32, 44);
  // tronco
  pix.box(13, 28, 6, 13, { fill: 't', light: 'T', dark: 'u' });
  pix.hline(10, 40, 12, 'u');
  // copa: discos sobrepostos, do escuro (fundo/direita) ao claro (alto/esquerda)
  pix.disc(16, 17, 14, 'd').disc(20, 20, 9, 'd');
  pix.disc(14, 15, 12, 'g').disc(9, 20, 6, 'g').disc(22, 12, 7, 'g');
  pix.disc(11, 11, 6, 'G').disc(18, 8, 4, 'G');
  pix.disc(9, 9, 2, 'H').px(16, 6, 'H').px(17, 6, 'H');
  // tufos de folha no contorno de baixo
  pix.pattern(6, 29, 20, 'd.dd.d');
  return pix.outline('o');
}

/** Elipse cheia: fonte, rodas, copas achatadas (Pix só tem círculo). */
function ellipse(pix: Pix, cx: number, cy: number, rx: number, ry: number, c: string): Pix {
  for (let y = Math.floor(cy - ry); y <= Math.ceil(cy + ry); y++) {
    for (let x = Math.floor(cx - rx); x <= Math.ceil(cx + rx); x++) {
      if (((x - cx) / rx) ** 2 + ((y - cy) / ry) ** 2 <= 1) pix.px(x, y, c);
    }
  }
  return pix;
}

/** Pinheiro: três camadas de copa em cone, cada uma mais larga que a de cima. */
function pine(): Pix {
  const pix = new Pix(16, 32);
  pix.box(7, 25, 2, 7, { fill: 't', light: 'T', dark: 'u' });
  const tiers: Array<[number, number, number]> = [
    [1, 13, 5],
    [8, 21, 7],
    [16, 27, 8],
  ];
  for (const [top, bottom, half] of tiers) {
    for (let y = top; y <= bottom; y++) {
      const r = Math.round(half * (0.35 + (0.65 * (y - top)) / (bottom - top)));
      for (let x = 8 - r; x < 8 + r; x++) {
        const c = y === bottom ? 'd' : x < 8 - r / 3 ? 'G' : x >= 8 + r / 2 ? 'd' : 'g';
        pix.px(x, y, c);
      }
    }
  }
  return pix.outline('o');
}

/** Árvore pequena e redonda (1 tile de pegada). */
function smallTree(): Pix {
  const pix = new Pix(16, 24);
  pix.box(7, 16, 2, 8, { fill: 't', light: 'T', dark: 'u' });
  ellipse(pix, 8, 9, 7, 7, 'd');
  ellipse(pix, 7, 8, 6, 6, 'g');
  ellipse(pix, 5, 6, 3, 3, 'G');
  pix.px(4, 4, 'H');
  return pix.outline('o');
}

/** Arbusto baixo e redondo. */
function shrub(): Pix {
  const pix = new Pix(16, 13);
  ellipse(pix, 8, 7, 7, 5, 'd');
  ellipse(pix, 7, 6, 6, 4, 'g');
  ellipse(pix, 5, 5, 2.5, 1.5, 'G');
  return pix.outline('o');
}

/**
 * Cerca-viva: as bordas esquerda e direita ficam abertas (sem contorno nem arredondamento),
 * então duas peças lado a lado viram uma sebe contínua. Padrão de 8 px: encaixa em 16.
 */
function hedge(): Pix {
  const pix = new Pix(16, 11);
  pix.hline(0, 0, 16, 'o').hline(0, 1, 16, 'G').rect(0, 2, 16, 7, 'g');
  pix.hline(0, 9, 16, 'd').hline(0, 10, 16, 'o');
  pix.pattern(0, 3, 16, '.G..d...').pattern(0, 6, 16, '..d.G...');
  return pix;
}

/** Canteiro de concreto com terra, folhagem e flores (2×1 tiles). */
function flowerBed(): Pix {
  const pix = new Pix(32, 18);
  pix.box(0, 4, 32, 14, { fill: 'c', light: 'l', dark: 'd' });
  pix.rect(2, 5, 28, 6, 's');
  for (let x = 3; x < 30; x += 5) pix.rect(x, 3, 4, 3, 'g').px(x + 1, 2, 'G');
  pix.px(5, 4, 'r').px(13, 3, 'y').px(20, 4, 'r').px(27, 3, 'y');
  return pix.outline('o');
}

/** Poste de rua: haste de metal, braço para a rua e luminária acesa à noite (glow). */
function lamp(): Pix {
  const pix = new Pix(16, 40);
  pix.box(7, 9, 2, 26, { fill: 'm', light: 'M', dark: 'n' });
  pix.box(4, 34, 8, 6, { fill: 'm', light: 'M', dark: 'n' });
  pix.box(8, 7, 5, 2, { fill: 'm', light: 'M', dark: 'n' });
  pix.box(11, 4, 4, 3, { fill: 'x', light: 'M', dark: 'n' });
  pix.rect(12, 6, 2, 1, 'L');
  return pix.outline('o');
}

/** Banco de praça de 2×1 tiles: encosto, assento de ripas e pés de metal. */
function bench(): Pix {
  const pix = new Pix(32, 18);
  pix.box(0, 0, 32, 6, { fill: 'w', light: 'L', dark: 'D' });
  pix.box(0, 6, 32, 7, { fill: 'w', light: 'L', dark: 'D' });
  pix.hline(1, 9, 30, 'D').hline(1, 11, 30, 'D');
  pix.rect(2, 13, 2, 5, 'm').rect(28, 13, 2, 5, 'm');
  return pix.outline('o');
}

/** Lixeira de rua: corpo verde com tampa de metal. */
function streetBin(): Pix {
  const pix = new Pix(12, 16);
  pix.box(1, 4, 10, 12, { fill: 'g', light: 'G', dark: 'd' });
  pix.hline(1, 8, 10, 'd').hline(1, 12, 10, 'd');
  pix.box(0, 2, 12, 3, { fill: 'm', light: 'M', dark: 'n' });
  return pix.outline('o');
}

/** Hidrante vermelho com bocais laterais e base de metal. */
function hydrant(): Pix {
  const pix = new Pix(10, 14);
  pix.box(2, 5, 6, 7, { fill: 'r', light: 'R', dark: 'd' });
  pix.box(3, 2, 4, 3, { fill: 'r', light: 'R', dark: 'd' });
  pix.rect(0, 7, 2, 2, 'r').rect(8, 7, 2, 2, 'r');
  pix.box(1, 12, 8, 2, { fill: 'm', light: 'M', dark: 'n' });
  return pix.outline('o');
}

/** Placa de rua azul com seta branca, em poste de metal. */
function streetSign(): Pix {
  const pix = new Pix(16, 28);
  pix.box(7, 12, 2, 16, { fill: 'm', light: 'M', dark: 'n' });
  pix.box(1, 2, 14, 10, { fill: 'b', light: 'l', dark: 'd' });
  pix.rect(3, 6, 6, 2, 'w').rect(9, 5, 1, 1, 'w').rect(9, 6, 2, 2, 'w').rect(9, 8, 1, 1, 'w');
  return pix.outline('o');
}

/** Fonte de 2×2 tiles: bacia circular de concreto com água. Os quadros mudam o jato e as ondas. */
function fountain(frame: number): Pix {
  const pix = new Pix(32, 24);
  ellipse(pix, 16, 13, 15, 10, 'd');
  ellipse(pix, 16, 11, 15, 9, 'c');
  ellipse(pix, 16, 11, 12.5, 7.5, 'x');
  ellipse(pix, 16, 11, 12, 7, 'W');
  ellipse(pix, 15, 10, 8, 4, 'w');
  const ripples: Array<[number, number]> = frame ? [[8, 14], [24, 13]] : [[10, 12], [22, 15]];
  for (const [x, y] of ripples) pix.px(x, y, 'w').px(x + 1, y, 'w');
  pix.rect(15, frame ? 4 : 3, 2, frame ? 6 : 8, 'w');
  return pix.outline('o');
}

/** Bicicletário de 2×1 tiles: barras em U de metal e duas bicicletas estacionadas. */
function bikeRack(): Pix {
  const pix = new Pix(32, 16);
  pix.hline(2, 3, 28, 'm');
  for (const x of [3, 11, 19, 27]) pix.vline(x, 3, 11, 'M').vline(x + 1, 3, 11, 'm');
  pix.disc(7, 11, 3, 'k').disc(23, 11, 3, 'k');
  pix.px(7, 11, 'M').px(23, 11, 'M');
  return pix.outline('o');
}

/** Carro de lado, virado para a direita (3×2 tiles). A cor do corpo vem da rampa escolhida. */
function car(): Pix {
  const pix = new Pix(48, 26);
  // cabine (teto no meio) e vidros
  pix.box(15, 2, 20, 10, { fill: 'B', light: 'L', dark: 'D' });
  pix.box(18, 4, 8, 6, { fill: 'g', light: 'G', dark: 'd' }).box(28, 4, 6, 6, { fill: 'g', light: 'G', dark: 'd' });
  pix.vline(26, 4, 6, 'B');
  // corpo: largura toda, luz em cima e sombra embaixo
  pix.box(1, 10, 46, 11, { fill: 'B', light: 'L', dark: 'D' });
  pix.hline(2, 19, 44, 'D');
  // faróis e lanternas
  pix.rect(44, 13, 2, 3, 'h').rect(1, 13, 2, 3, 'r');
  // rodas com calota
  pix.disc(11, 21, 4, 'k').disc(37, 21, 4, 'k');
  pix.disc(11, 21, 2, 'm').disc(37, 21, 2, 'm');
  return pix.outline('o');
}

function carro(cor: RampName, id: string, name: string): FurnitureDef {
  return {
    id,
    name,
    category: 'exterior',
    footprint: { w: 3, h: 2 },
    sprite: car().sprite({
      o: `${cor}0`,
      B: `${cor}2`,
      L: `${cor}3`,
      D: `${cor}1`,
      g: 'glass2',
      G: 'glass3',
      d: 'glass0',
      h: 'light3',
      r: 'red3',
      k: 'black1',
      m: 'metal3',
    }),
  };
}

/** Moto de lado, 2×1 tiles: tanque vermelho, guidão e rodas. */
function moto(): Pix {
  const pix = new Pix(24, 20);
  pix.hline(5, 14, 14, 'm');
  pix.box(8, 6, 8, 6, { fill: 'R', light: 'S', dark: 'T' });
  pix.rect(4, 5, 5, 2, 'k');
  pix.vline(17, 3, 9, 'M').hline(15, 3, 4, 'M');
  pix.vline(18, 9, 5, 'm');
  pix.disc(5, 15, 4, 'k').disc(19, 15, 4, 'k').disc(5, 15, 2, 'M').disc(19, 15, 2, 'M');
  pix.disc(22, 9, 1.5, 'h');
  return pix.outline('o');
}

/** Caixa de correio azul em pedestal de metal. */
function mailbox(): Pix {
  const pix = new Pix(12, 22);
  pix.rect(5, 14, 2, 8, 'm');
  pix.box(1, 6, 10, 9, { fill: 'b', light: 'l', dark: 'd' });
  pix.rect(3, 9, 6, 1, 'k').px(10, 11, 'r');
  return pix.outline('o');
}

const TREE = { d: 'leaf1', g: 'leaf2', G: 'leaf3', H: 'light3', t: 'trunk2', T: 'trunk3', u: 'trunk1' };
const PINE = { o: 'leaf0', d: 'leaf1', g: 'leaf2', G: 'leaf3', t: 'trunk2', T: 'trunk3', u: 'trunk1' };
const SHRUB = { o: 'leaf0', d: 'leaf1', g: 'leaf2', G: 'leaf3' };
const HEDGE = { o: 'leaf0', d: 'leaf1', g: 'leaf2', G: 'leaf3' };
const FLOWERS = { o: 'concrete0', c: 'concrete2', l: 'concrete3', d: 'concrete1', s: 'trunk1', g: 'leaf2', G: 'leaf3', r: 'red2', y: 'light2' };
const LAMP = { o: 'metal0', m: 'metal2', M: 'metal3', n: 'metal1', x: 'metal1', L: 'light3' };
const BENCH = { o: 'wood0', w: 'wood2', L: 'wood3', D: 'wood1', m: 'metal2' };
const BIN = { o: 'fabricGreen0', g: 'fabricGreen2', G: 'fabricGreen3', d: 'fabricGreen1', m: 'metal2', M: 'metal3', n: 'metal1' };
const HYDRANT = { o: 'red0', r: 'red2', R: 'red3', d: 'red1', m: 'metal2', M: 'metal3', n: 'metal1' };
const SIGN = { o: 'black0', m: 'metal2', M: 'metal3', n: 'metal1', b: 'fabricBlue2', l: 'fabricBlue3', d: 'fabricBlue1', w: 'white3' };
const FOUNTAIN = { o: 'concrete0', d: 'concrete1', c: 'concrete2', x: 'water1', W: 'water2', w: 'water3' };
const RACK = { o: 'metal0', m: 'metal2', M: 'metal3', k: 'black1' };
const MOTO = { o: 'red0', R: 'red2', S: 'red3', T: 'red1', k: 'black1', m: 'metal2', M: 'metal3', h: 'light3' };
const MAILBOX = { o: 'fabricBlue0', b: 'fabricBlue2', l: 'fabricBlue3', d: 'fabricBlue1', m: 'metal2', k: 'black0', r: 'red2' };

export const EXTERIOR: readonly FurnitureDef[] = [
  {
    id: 'arvore',
    name: 'Árvore',
    category: 'exterior',
    footprint: { w: 2, h: 2 },
    sprite: tree().sprite({ o: 'leaf0', ...TREE }),
  },
  {
    id: 'arvore-pinheiro',
    name: 'Pinheiro',
    category: 'exterior',
    footprint: { w: 1, h: 1 },
    sprite: pine().sprite(PINE),
  },
  {
    id: 'arvore-pequena',
    name: 'Árvore pequena',
    category: 'exterior',
    footprint: { w: 1, h: 1 },
    sprite: smallTree().sprite({ o: 'leaf0', ...TREE }),
  },
  {
    id: 'arbusto',
    name: 'Arbusto',
    category: 'exterior',
    footprint: { w: 1, h: 1 },
    sprite: shrub().sprite(SHRUB),
  },
  {
    id: 'cerca-viva',
    name: 'Cerca-viva',
    category: 'exterior',
    footprint: { w: 1, h: 1 },
    sprite: hedge().sprite(HEDGE),
  },
  {
    id: 'canteiro-flores',
    name: 'Canteiro de flores',
    category: 'exterior',
    footprint: { w: 2, h: 1 },
    sprite: flowerBed().sprite(FLOWERS),
  },
  {
    id: 'poste',
    name: 'Poste de rua',
    category: 'exterior',
    footprint: { w: 1, h: 1 },
    sprite: lamp().sprite(LAMP),
    glow: { color: 'rgba(255, 220, 140, 0.35)', radius: 26, dy: -14 },
  },
  {
    id: 'banco-praca',
    name: 'Banco de praça',
    category: 'exterior',
    footprint: { w: 2, h: 1 },
    sprite: bench().sprite(BENCH),
    sortBias: -4,
    spots: [
      { dx: 0, dy: 0, facing: 'down', kind: 'sofa', use: 'rest' },
      { dx: 1, dy: 0, facing: 'down', kind: 'sofa', use: 'rest' },
    ],
  },
  {
    id: 'lixeira-rua',
    name: 'Lixeira de rua',
    category: 'exterior',
    footprint: { w: 1, h: 1 },
    sprite: streetBin().sprite(BIN),
  },
  {
    id: 'hidrante',
    name: 'Hidrante',
    category: 'exterior',
    footprint: { w: 1, h: 1 },
    sprite: hydrant().sprite(HYDRANT),
  },
  {
    id: 'placa-rua',
    name: 'Placa de rua',
    category: 'exterior',
    footprint: { w: 1, h: 1 },
    sprite: streetSign().sprite(SIGN),
  },
  {
    id: 'fonte',
    name: 'Fonte',
    category: 'exterior',
    footprint: { w: 2, h: 2 },
    sprite: fountain(0).sprite(FOUNTAIN),
    frames: [fountain(1).sprite(FOUNTAIN)],
    frameMs: 700,
  },
  {
    id: 'bicicletario',
    name: 'Bicicletário',
    category: 'exterior',
    footprint: { w: 2, h: 1 },
    sprite: bikeRack().sprite(RACK),
  },
  carro('red', 'carro-vermelho', 'Carro vermelho'),
  carro('fabricBlue', 'carro-azul', 'Carro azul'),
  carro('white', 'carro-branco', 'Carro branco'),
  carro('black', 'carro-preto', 'Carro preto'),
  {
    id: 'moto',
    name: 'Moto',
    category: 'exterior',
    footprint: { w: 2, h: 1 },
    sprite: moto().sprite(MOTO),
  },
  {
    id: 'caixa-correio',
    name: 'Caixa de correio',
    category: 'exterior',
    footprint: { w: 1, h: 1 },
    sprite: mailbox().sprite(MAILBOX),
  },
];
